import { useEffect, useState } from 'react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Camera, X as XIcon, ArrowLeft, Loader2 } from 'lucide-react';
import SignatureCanvas from '@/shared/components/SignatureCanvas';
import BlocMandat from './BlocMandat';
import BlocConsentements from './BlocConsentements';
import ChoixAgregateurMandat from '@/shared/components/ChoixAgregateurMandat';

/**
 * Fin de chantier : LE MANDAT, LES CONSENTEMENTS ET LA RÉCEPTION, SUR UNE SEULE PAGE,
 * SOUS UNE SEULE SIGNATURE.
 *
 * ⚠️ CETTE PAGE DÉFAIT DÉLIBÉRÉMENT UNE DÉCISION DE TROIS JOURS, ET CE N'EST PAS UN
 * OUBLI QU'ON RÉPARE.
 *
 * Le 27/08, sur demande de David, le lot C avait éclaté l'écran de signature unique en
 * quatre étapes : mandat (4), fin de chantier (5), consentements (6), confirmation (7),
 * avec DEUX signatures distinctes. Ce n'était pas un hasard, c'était le correctif d'un
 * défaut réel — on faisait signer la réception du chantier avant d'avoir le mandat, et
 * une signature unique pour deux engagements juridiques ne prouvait ni l'un ni l'autre.
 *
 * Le 30/08 au soir, toujours David, toujours via Boubacar : le mandat et les
 * consentements descendent ici, et une seule signature couvre l'ensemble. Cette demande
 * REMPLACE celle du 27/08.
 *
 * Un lecteur qui trouve dans six mois une signature là où le code en attendait deux
 * doit comprendre que c'est voulu. C'est aussi pourquoi le serveur ÉCRIT ce que la
 * signature couvre (`signature_portee`) : les dossiers signés entre le 27 et le 30 août
 * portent deux signatures distinctes et doivent rester lisibles tels qu'ils ont été
 * signés.
 *
 * L'ORDRE DE LECTURE EST CELUI DE L'ENGAGEMENT. Le client voit ce qu'il mandate, puis
 * ce qu'il autorise, puis ce qu'il réceptionne, et il signe en dernier. La critique du
 * 27/08 — « le client donne ses accords après avoir signé la fin de ses travaux » — ne
 * tient plus : il ne signe plus qu'à la fin, après avoir tout lu.
 *
 * LE LIBELLÉ DE LA SIGNATURE A ÉTÉ ARRÊTÉ LE 31/08. Il nomme les trois actes — c'est la
 * règle non négociable — et il les nomme en langage de client : « les autorisations que
 * je viens de cocher », pas « les consentements ». Deux formulations plus courtes ont
 * été écartées à ce motif. Le raccourcir défairait l'arbitrage.
 *
 * Présentationnel : il ne sait ni enregistrer ni naviguer.
 */
const QUESTIONS = [
  { id: 'chantier_propre', text: "Le chantier a-t-il été laissé propre après l'installation ?" },
  { id: 'equipe_pose', text: 'Êtes-vous satisfait(e) de l’équipe de pose ?' },
  { id: 'emplacement', text: "L'emplacement des différents éléments vous convient-il ?" },
  { id: 'satisfait_installation', text: 'Êtes-vous satisfait(e) de votre installation ?' },
];

export default function StepFinChantier({
  client, enabledEquipements, referenceChantier,
  satisfaction, setSatisfaction,
  photos, handlePhotoAdd, removePhoto,
  consents, setConsents,
  agregateur, setAgregateur,
  signature, setSignature, materielEnAttente,
  error, valide, signing, handleSign, setStep,
}) {
  // ⚠️ `attestationFavorable` VIVAIT ICI, ET SON RETRAIT EST VOULU.
  //
  // Il valait vrai quand les quatre questions étaient à Oui, et pré-cochait alors les
  // trois clauses commerciales du mandat (arbitrage de Boubacar du 31/08). Ces trois
  // clauses — `rgpd`, `cgv`, `exclusivite` — sont exactement celles qu'il a fait retirer
  // le 02/09 : la condition ne pilotait plus rien. La calculer encore aurait laissé
  // croire à un mécanisme actif, et l'aurait réarmé tout seul le jour où une clause
  // complémentaire reviendrait.
  //
  // Ce qu'elle apportait — ne pas cocher sept cases à la main devant le client — est
  // rendu par la case « tout cocher », qui est un geste du client et non un état posé à
  // sa place. `QUESTIONS` reste : l'attestation, elle, n'a pas bougé.

  // Lot L4 : une signature REPRISE est celle qui existait quand l'étape s'est affichée
  // (retour après un refus du serveur). Le canevas renvoie aussi la signature à chaque
  // trait d'une première signature : celle-là ne doit rien annoncer de « conservé ».
  const [signatureReprise, setSignatureReprise] = useState(() => Boolean(signature));
  // Effacée, elle n'est plus reprise : la suivante est un nouveau geste du client.
  useEffect(() => { if (!signature) setSignatureReprise(false); }, [signature]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="step-fin-chantier">
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">Fin de chantier</h2>
        {/* ⚠️ CE SOUS-TITRE A ÉTÉ RÉDUIT LE 02/09, ET IL NE SE RALLONGE PAS.
            Il annonçait « Le mandat, les autorisations et la réception du chantier,
            puis une signature. » — un sommaire de la page, écrit au-dessus de la page
            elle-même, qui redisait en vocabulaire de produit ce que le libellé de la
            signature dit déjà en langage de client, quelques centimètres plus bas.
            Demande de Boubacar : sous « Fin de chantier », « Signature requise », et
            rien d'autre. Ce qui reste ici est une CONSIGNE au poseur — il manque une
            signature pour finir — pas un résumé de ce que le client engage. Ce
            dernier point est dit une seule fois, à l'endroit qui l'engage.

            ⚠️ « Signature requise » existe ailleurs avec un AUTRE sens : c'est le
            `detail` d'un HTTP 400 du canal QR, dans
            `backend/app/modules/onboarding/routes.py`. Les deux ne se factorisent
            pas : ici c'est un titre d'écran, là-bas un refus d'API.

            Le `data-testid` n'est pas décoratif : la recette d'écran lit CE texte, et
            un sélecteur de texte se casserait au premier changement de casse. */}
        <p className="text-[12px] text-neutral-500" data-testid="fin-chantier-sous-titre">
          Signature requise
        </p>
      </div>

      {/* Le poseur part avec un dossier incomplet : il doit le savoir MAINTENANT, pas
          au prochain audit. Le chantier aboutit quand même — c'est la décision du
          30/08 — et il apparaîtra dans « en attente de matériel ». */}
      {materielEnAttente && (
        <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-[12px] text-amber-800"
             data-testid="avertissement-materiel-en-attente">
          Un équipement au moins n&apos;a pas répondu. Le chantier se termine quand même
          et partira <span className="font-medium">en attente de matériel</span> : il
          restera à rattacher l&apos;appareil depuis le bureau.
        </div>
      )}

      {/* 1. Ce que le client mandate */}
      <BlocMandat
        client={client}
        enabledEquipements={enabledEquipements}
        referenceChantier={referenceChantier}
      />

      {/* 2. CE QU'IL RÉCEPTIONNE — REMONTÉ ICI LE 31/08.
          Demande de Boubacar : « le formulaire de l'attestation de fin de travaux doit
          remonter en premier, ensuite les consentements et ensuite les signatures ».
          L'ordre suit maintenant le déroulé réel du chantier : on constate le travail
          fait, PUIS on demande les autorisations, PUIS on signe. */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-4">
        <p className="text-[12px] font-medium text-neutral-700">Attestation de fin de travaux</p>
        {QUESTIONS.map(q => (
          <div key={q.id} className="space-y-2 pb-3 border-b border-neutral-100 last:border-0 last:pb-0">
            <p className="text-[12px] text-neutral-700 font-medium">{q.text}</p>
            <div className="flex items-center gap-4">
              {['Oui', 'Non'].map(reponse => (
                <label key={reponse} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name={q.id}
                    checked={satisfaction[q.id]?.answer === reponse}
                    onChange={() => setSatisfaction(p => ({ ...p, [q.id]: { ...p[q.id], answer: reponse } }))}
                    className="w-4 h-4 accent-marque"
                    data-testid={`satisfaction-${q.id}-${reponse.toLowerCase()}`}
                  />
                  <span className="text-[12px] text-neutral-600">{reponse}</span>
                </label>
              ))}
            </div>
            <Input
              className="h-8 text-[11px]"
              placeholder="Commentaire (optionnel)"
              value={satisfaction[q.id]?.comment || ''}
              onChange={e => setSatisfaction(p => ({ ...p, [q.id]: { ...p[q.id], comment: e.target.value } }))}
              data-testid={`satisfaction-${q.id}-comment`}
            />
          </div>
        ))}
      </div>

      {/* 3. Ce qu'il autorise. Aucune case n'est cochée d'avance : la case « tout
          cocher » du bloc est un geste du client, jamais un état posé à sa place — voir
          `ClausesConsentement`.

          ⚠️ CE BLOC RECEVAIT `consentPdl` ET `pdlSaisi`, ET LEUR RETRAIT EST VOULU
          (10/09/2026). Ils alimentaient la carte de consentement Energrid, que Boubacar a
          fait sauter de cet écran. Le consentement lui-même n'a pas disparu : il part
          toujours après la signature, envoyé par le parcours — voir
          `InstallateurPage.handleSign` et
          `docs/decisions/2026-09-10-retrait-de-la-clause-energrid.md`. */}
      <BlocConsentements
        consents={consents}
        setConsents={setConsents}
      />

      {/* L'agregateur du mandat, AVANT la signature (C8a) : il decide de la piece signee et
          de l'agregateur auquel ces equipements pourront etre declares. */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <ChoixAgregateurMandat valeur={agregateur} onChange={setAgregateur}
                               testid="terrain-agregateur-mandat" />
      </div>

      {/* Photos de l'installation (max 3) */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3" data-testid="photos-section">
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-medium text-neutral-700">Photos de l&apos;installation <span className="text-neutral-400 font-normal">(optionnel, max 3)</span></p>
          <span className="text-[11px] text-neutral-400">{photos.length}/3</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((ph, idx) => (
            <div key={idx} className="relative aspect-square rounded-md overflow-hidden bg-neutral-100 border border-neutral-200" data-testid={`photo-preview-${idx}`}>
              <img src={ph.data} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(idx)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"
                data-testid={`photo-remove-${idx}`}
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
          {photos.length < 3 && (
            <label className="aspect-square rounded-md border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center gap-1 text-neutral-500 hover:border-marque hover:text-marque cursor-pointer transition-colors" data-testid="photo-add-label">
              <Camera className="w-5 h-5" />
              <span className="text-[10px]">Ajouter</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={handlePhotoAdd}
                data-testid="photo-input"
              />
            </label>
          )}
        </div>
        {photos.length > 0 && (
          <p className="text-[10px] text-neutral-400">Les photos sont compressées automatiquement (1024 px max, qualité 80 %) et intégrées au PDF de l&apos;attestation.</p>
        )}
      </div>

      {/* 4. UNE signature, qui NOMME les trois choses qu'elle engage.
          Un libellé qui ne parlerait que du mandat deviendrait faux le jour où il en
          couvre trois — et ce jour est arrivé le 30/08.

          ⚠️ CE TEXTE EST CELUI QUE BOUBACAR A ARRÊTÉ LE 31/08, et il est volontairement
          plus long qu'il ne pourrait l'être. Deux formulations plus courtes avaient été
          proposées ; celle-ci a été retenue parce qu'elle nomme les trois actes EN
          LANGAGE DE CLIENT — « les autorisations que je viens de cocher » plutôt que
          « les consentements » — et non en vocabulaire de produit. Sur une pièce que des
          particuliers signent, l'ambiguïté se paie plus cher que la longueur.

          Le raccourcir « pour que ça tienne » reviendrait donc à défaire l'arbitrage. */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4" data-testid="signature-unique">
        <p className="text-[12px] font-medium text-neutral-700 mb-1">Signature du client</p>
        <p className="text-[11px] text-neutral-500 mb-2" data-testid="signature-portee">
          En signant ci-dessous, je signe d&apos;un seul geste : le mandat de flexibilité,
          les autorisations que je viens de cocher, et la réception de mon chantier.
        </p>
        {/* Lot L4 : apres un refus du serveur (numero d'appareil deja pris), le poseur
            corrige l'ID et revient ici. Le trait du client est conserve : on le montre,
            pour qu'il ne le fasse pas resigner. */}
        {signatureReprise && signature && (
          <p className="text-[11px] text-emerald-700 mb-2" data-testid="signature-conservee">
            Signature conservée : inutile de faire signer à nouveau, sauf si vous l&apos;effacez.
          </p>
        )}
        <SignatureCanvas onSignatureChange={setSignature} width={460} height={160}
          valeurInitiale={signatureReprise ? signature : ''} />
      </div>

      {error && <div className="p-2.5 rounded-md bg-red-50 border border-red-100 text-red-700 text-[12px]" role="alert" data-testid="inst-error">{error}</div>}

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1 h-12 text-[14px]"
                onClick={() => setStep(3)} data-testid="fin-chantier-retour">
          <ArrowLeft className="w-5 h-5 mr-1.5" /> Retour
        </Button>
        <Button className="flex-1 h-12 text-[15px]" disabled={!valide || signing}
                onClick={handleSign} data-testid="fin-chantier-terminer">
          {signing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Envoi...</> : 'Terminer le chantier'}
        </Button>
      </div>
    </div>
  );
}
